import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarCheck, Download, PhoneCall, Plane } from "lucide-react";
import { api } from "@/lib/api";
import { Price } from "@/lib/currency";
import { waLink } from "@/data/content";
import { FadeUp, SectionHeading } from "./Reveal";

const slugId = (p) => p._id || p.id;

const GuideCardMini = ({ p, i }) => (
  <FadeUp key={slugId(p)} delay={i * 0.05}>
    <Link
      to={`/shop/p/${slugId(p)}`}
      data-testid={`home-guide-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="group flex h-full flex-col overflow-hidden border border-jawa-espresso/10 bg-white transition-colors hover:border-jawa-coral/60"
    >
      <div className="relative h-44 overflow-hidden">
        <img src={p.image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute left-4 top-4 bg-jawa-coral px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
          Instant PDF
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-serif text-xl font-medium text-jawa-espresso">{p.title}</h3>
        <div className="mt-4 flex items-center justify-between border-t border-jawa-stone pt-4">
          <span className="font-serif text-2xl font-medium text-jawa-espresso" data-testid={`home-guide-price-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <Price amount={p.price} from={p.currency || "USD"} />
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-coral group-hover:text-jawa-espresso">
            Buy now <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  </FadeUp>
);

const FREE_RESOURCES = [
  { label: "Nigerian Airport Guide", note: "Free PDF — arrivals, SIMs, pickups", href: "/guides", testid: "home-free-airport" },
  { label: "Best Nigerian Private School Guide", note: "Free PDF — choosing well", href: "/guides", testid: "home-free-school" },
  { label: "Emergency Contacts", note: "Free — on-page, no download", href: "/emergency", testid: "home-free-emergency" },
  { label: "Free A–Z Nigeria", note: "Free — read online", href: "/guides", testid: "home-free-az" },
];

export const FeaturedSales = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  const guides = products.filter((p) => p.category === "guide" && p.checkout !== "enquiry");
  const flagship = guides.filter((p) => !p.title.startsWith("Detty December"));
  const detty = guides.filter((p) => p.title.startsWith("Detty December"));

  return (
    <section id="guides" data-testid="featured-guides-section" className="border-b border-jawa-stone bg-jawa-ivory">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <FadeUp className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <SectionHeading overline="The JÀWÀ A–Z Guides" title="Know before you land." />
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-jawa-espresso/70">
              Researched, practical, instantly downloadable. Facts separated from estimates — no invented
              returns, no guesswork sold as certainty.
            </p>
          </div>
          <Link to="/guides" data-testid="home-guides-hub-link" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-turquoise transition-colors hover:text-jawa-espresso">
            All guides & free resources <ArrowUpRight className="h-4 w-4" />
          </Link>
        </FadeUp>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-flagship-guides">
          {flagship.map((p, i) => (
            <GuideCardMini key={slugId(p)} p={p} i={i} />
          ))}
        </div>

        {detty.length > 0 && (
          <div className="mt-6 grid gap-6 sm:grid-cols-2" data-testid="home-detty-guides">
            {detty.map((p, i) => (
              <GuideCardMini key={slugId(p)} p={p} i={i} />
            ))}
          </div>
        )}

        <FadeUp className="mt-10 grid gap-px overflow-hidden border border-jawa-turquoise/40 bg-jawa-turquoise/40 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-free-resources">
          {FREE_RESOURCES.map((r) => (
            <Link key={r.testid} to={r.href} data-testid={r.testid} className="group flex items-center gap-3 bg-white p-5 transition-colors hover:bg-jawa-ivory">
              {r.href === "/emergency" ? <PhoneCall className="h-5 w-5 shrink-0 text-jawa-coral" /> : <Download className="h-5 w-5 shrink-0 text-jawa-turquoise" />}
              <span>
                <span className="block text-sm font-semibold text-jawa-espresso group-hover:text-jawa-coral">{r.label}</span>
                <span className="block text-[11px] uppercase tracking-[0.15em] text-jawa-espresso/50">{r.note}</span>
              </span>
            </Link>
          ))}
        </FadeUp>
      </div>
    </section>
  );
};

export const VipArrival = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.category === "vip"))).catch(() => {});
  }, []);

  return (
    <section id="vip-arrival" data-testid="vip-arrival-section" className="bg-jawa-nearblack">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <FadeUp>
            <div className="relative overflow-hidden">
              <img src="/lagos-airport-terminal.jpg" alt="Murtala Muhammed International Airport, Lagos" loading="lazy" className="h-full max-h-[560px] w-full object-cover" />
              <span className="absolute left-4 top-4 bg-jawa-gold px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-jawa-nearblack">
                Make your arrival VIP
              </span>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="text-xs uppercase tracking-[0.35em] text-jawa-gold">JÀWÀ VIP Arrival Experience</p>
            <h2 className="mt-4 font-serif text-4xl font-medium tracking-tight text-jawa-ivory sm:text-5xl">
              Step off the plane into handled.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-jawa-ivory/70">
              Four arrival packages — from the thoughtful Welcome Box to the full Private Client Welcome with
              chauffeur-driven pickup and airport concierge assistance. Every package includes seven days of
              WhatsApp concierge support from your arrival. Book online; gift contents are confirmed with you
              before fulfilment.
            </p>
            <div className="mt-8 space-y-3" data-testid="vip-products">
              {products.map((p) => (
                <Link
                  key={slugId(p)}
                  to={`/shop/p/${slugId(p)}`}
                  data-testid={`vip-product-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group flex w-full items-center justify-between gap-4 border border-jawa-ivory/15 p-5 text-left transition-colors hover:border-jawa-gold/60"
                >
                  <span>
                    <span className="block font-serif text-lg text-jawa-ivory group-hover:text-jawa-gold">{p.title}</span>
                    <span className="mt-0.5 block text-[11px] uppercase tracking-[0.15em] text-jawa-ivory/45">Book online — 7-day WhatsApp concierge included</span>
                  </span>
                  <span className="shrink-0 font-serif text-xl text-jawa-gold">
                    from <Price amount={p.price} from={p.currency || "USD"} className="text-jawa-gold" />
                    <span className="ml-1 text-[10px] uppercase tracking-[0.15em] text-jawa-ivory/40">pp</span>
                  </span>
                </Link>
              ))}
            </div>
            <p className="mt-5 text-xs leading-relaxed text-jawa-ivory/45">
              Welcome Box arrival pickup is an optional add-on; the other three packages include chauffeur-driven
              airport pickup. Seasonal and destination rules are shown on each package before you pay.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const SHOP_HIGHLIGHTS = ["JÀWÀ T-shirt", "JÀWÀ Tote Bag", "Original A4 Artwork by a Nigerian Artist", "Detty December Essentials"];

export const ShopPreview = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const highlights = SHOP_HIGHLIGHTS.map((t) => products.find((p) => p.title === t)).filter(Boolean);

  return (
    <section id="shop" data-testid="shop-preview-section" className="border-y border-jawa-stone bg-white">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <FadeUp className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <SectionHeading overline="The JÀWÀ Shop" title="Wear it. Carry it. Gift it." />
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-jawa-espresso/70">
              A curated Nigerian lifestyle shop — the JÀWÀ tee and tote, travel accessories, original artwork
              by credited Nigerian artists and gift bundles. Small batches, confirmed at order.
            </p>
          </div>
          <Link to="/shop" data-testid="home-shop-link" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-turquoise transition-colors hover:text-jawa-espresso">
            Browse the full shop <ArrowUpRight className="h-4 w-4" />
          </Link>
        </FadeUp>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-shop-highlights">
          {highlights.map((p, i) => (
            <FadeUp key={slugId(p)} delay={i * 0.05}>
              <Link
                to={`/shop/p/${slugId(p)}`}
                data-testid={`home-shop-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group flex h-full flex-col justify-between border border-jawa-espresso/12 bg-jawa-ivory p-7 transition-colors hover:border-jawa-gold/70"
              >
                <h3 className="font-serif text-xl font-medium leading-snug text-jawa-espresso group-hover:text-jawa-coral">{p.title}</h3>
                <div className="mt-6 flex items-center justify-between">
                  <span className="font-serif text-2xl font-medium text-jawa-espresso">
                    <Price amount={p.price} from={p.currency || "GBP"} />
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-jawa-coral" />
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ConsultStrip = () => (
  <section data-testid="consult-strip" className="border-b border-jawa-stone bg-jawa-ivory">
    <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-6 py-10 lg:px-10">
      <div className="flex items-center gap-4">
        <CalendarCheck className="h-6 w-6 text-jawa-coral" />
        <p className="max-w-xl text-sm leading-relaxed text-jawa-espresso/70">
          <span className="font-semibold text-jawa-espresso">1-2-1 consultations</span> — 15 to 60 minutes on a
          private video call, booked in West Africa Time. From <Price amount={25} from="USD" />.
        </p>
      </div>
      <div className="flex gap-3">
        <Link to="/consultations" data-testid="consult-strip-book" className="bg-jawa-espresso px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-coral">
          Book a consultation
        </Link>
        <a href={waLink("Hello JÀWÀ — I'd like to plan my arrival.")} target="_blank" rel="noopener noreferrer" data-testid="consult-strip-whatsapp"
          className="inline-flex items-center gap-2 border border-jawa-espresso/25 px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso transition-colors hover:border-jawa-turquoise">
          <Plane className="h-4 w-4" /> WhatsApp
        </a>
      </div>
    </div>
  </section>
);
